import React, { useState } from 'react';
import { ArrowLeft, Filter, User, Building, AlertTriangle, Briefcase, MapPin, UserCheck, Calendar } from 'lucide-react';
import { EmptyState } from './EmptyState';
import { GuardRosterModal } from './GuardRosterModal';
import { ActionPanel } from './ActionPanel';
interface Guard {
  id: string;
  name: string;
  shifts: number;
  grade: string;
  site: string;
  province: string;
}
interface GuardsOverThresholdListProps {
  onBack: () => void;
}
const guards: Guard[] = [{
  id: '1',
  name: 'Sipho Dlamini',
  shifts: 24,
  grade: 'Grade A',
  site: 'Woolworths HQ',
  province: 'Western Cape'
}, {
  id: '2',
  name: 'Thabo Molefe',
  shifts: 22,
  grade: 'Grade B',
  site: 'Standard Bank Tower',
  province: 'Gauteng'
}, {
  id: '3',
  name: 'Nomsa Khumalo',
  shifts: 21,
  grade: 'Grade C',
  site: 'Pick n Pay Constantia',
  province: 'Western Cape'
}, {
  id: '4',
  name: 'Lerato Nkosi',
  shifts: 25,
  grade: 'Grade A',
  site: 'Sandton City Mall',
  province: 'Gauteng'
}, {
  id: '5',
  name: 'Bongani Zulu',
  shifts: 20,
  grade: 'Grade B',
  site: 'Durban ICC',
  province: 'KwaZulu-Natal'
}, {
  id: '6',
  name: 'Precious Mokoena',
  shifts: 23,
  grade: 'Grade A',
  site: 'OR Tambo Airport',
  province: 'Gauteng'
}, {
  id: '7',
  name: 'Mandla Sithole',
  shifts: 21,
  grade: 'Grade C',
  site: 'Gateway Theatre',
  province: 'KwaZulu-Natal'
}, {
  id: '8',
  name: 'Zanele Mthembu',
  shifts: 24,
  grade: 'Grade B',
  site: 'V&A Waterfront',
  province: 'Western Cape'
}];
export function GuardsOverThresholdList({
  onBack
}: GuardsOverThresholdListProps) {
  const [selectedProvince, setSelectedProvince] = useState<string>('All Provinces');
  const [selectedGuard, setSelectedGuard] = useState<Guard | null>(null);
  const [actionGuard, setActionGuard] = useState<Guard | null>(null);
  const filteredGuards = selectedProvince === 'All Provinces' ? guards : guards.filter(guard => guard.province === selectedProvince);
  const getStatus = (shifts: number) => {
    if (shifts >= 23) return {
      label: 'Critical',
      color: 'bg-rose-500 text-white'
    };
    return {
      label: 'Warning',
      color: 'bg-amber-500 text-white'
    };
  };
  const handleViewRoster = (guard: Guard) => {
    setSelectedGuard(guard);
  };
  const handleCloseRoster = () => {
    setSelectedGuard(null);
  };
  const handleTakeAction = (guard: Guard) => {
    setActionGuard(guard);
  };
  const handleCloseAction = () => {
    setActionGuard(null);
  };
  const provinces = ['All Provinces', 'Western Cape', 'Gauteng', 'KwaZulu-Natal'];
  return <>
      <div className="flex flex-col space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-702">
        {/* Header Section */}
        <div className="flex flex-col space-y-4" data-id="element-703">
          <button onClick={onBack} className="group flex w-fit items-center text-sm font-medium text-[#5a7280] hover:text-[#062133] transition-colors min-h-[44px] px-2 -ml-2 rounded-lg hover:bg-[#062133]/5" aria-label="Back to dashboard" data-id="element-704">
            <ArrowLeft className="mr-1 h-4 w-4 transition-transform group-hover:-translate-x-1" data-id="element-705" />
            Back to Dashboard
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-706">
            <div data-id="element-707">
              <h2 className="text-2xl font-bold text-[#062133]" data-id="element-708">
                Guards Over Threshold ({guards.length})
              </h2>
              <p className="text-[#5a7280]" data-id="element-709">
                Guards exceeding the 19-shift monthly threshold
              </p>
            </div>

            {/* Filter Dropdown */}
            <div className="relative" data-id="element-710">
              <div className="flex items-center space-x-2" data-id="element-711">
                <Filter className="h-4 w-4 text-[#5a7280]" data-id="element-712" />
                <select value={selectedProvince} onChange={e => setSelectedProvince(e.target.value)} className="block w-full rounded-md border-[#e5e5e5] py-2 pl-3 pr-10 text-base focus:border-[#062133] focus:outline-none focus:ring-[#062133] sm:text-sm bg-white border shadow-sm text-[#062133] min-h-[44px]" data-id="element-713">
                  {provinces.map(province => <option key={province} value={province} data-id="element-714">
                      {province}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-sm" data-id="element-715">
          <table className="min-w-full divide-y divide-[#e5e5e5]" data-id="element-716">
            <thead className="bg-[#fafaf9]" data-id="element-717">
              <tr data-id="element-718">
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-719">
                  Guard Name
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-720">
                  Current Shifts
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-721">
                  Grade
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-722">
                  Primary Site
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-723">
                  Status
                </th>
                <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-724">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#e5e5e5]" data-id="element-725">
              {filteredGuards.map(guard => {
              const status = getStatus(guard.shifts);
              return <tr key={guard.id} className="hover:bg-[#fafaf9] transition-colors cursor-pointer" onClick={() => handleViewRoster(guard)} data-id="element-726">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-727">
                      <div className="flex items-center" data-id="element-728">
                        <div className="h-8 w-8 rounded-full bg-[#062133]/5 flex items-center justify-center mr-3" data-id="element-729">
                          <User className="h-4 w-4 text-[#062133]" data-id="element-730" />
                        </div>
                        <div className="text-sm font-semibold text-[#062133]" data-id="element-731">
                          {guard.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-732">
                      <div className="text-sm font-medium text-[#062133]" data-id="element-733">
                        {guard.shifts} shifts
                      </div>
                      <div className="text-xs text-[#5a7280]" data-id="element-734">
                        {guard.shifts - 19} over threshold
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-735">
                      <div className="flex items-center text-sm text-[#5a7280]" data-id="element-736">
                        <Briefcase className="mr-1.5 h-4 w-4" data-id="element-737" />
                        {guard.grade}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-738">
                      <div className="flex items-center text-sm text-[#062133]" data-id="element-739">
                        <Building className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-740" />
                        {guard.site}
                      </div>
                      <div className="flex items-center text-xs text-[#5a7280] mt-0.5 ml-5.5" data-id="element-741">
                        <MapPin className="mr-1 h-3 w-3" data-id="element-742" />
                        {guard.province}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-743">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`} data-id="element-744">
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-745">
                      <button onClick={e => {
                    e.stopPropagation();
                    handleViewRoster(guard);
                  }} className="inline-flex items-center px-3 py-1.5 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-md text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[32px]" data-id="element-746">
                        <Calendar className="mr-1.5 h-3.5 w-3.5" data-id="element-747" />
                        View Roster
                      </button>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
          {filteredGuards.length === 0 && <div className="p-0 border-none" data-id="element-748">
              <EmptyState icon={UserCheck} title="No guards over threshold" description="All guards are within the 19-shift monthly limit" data-id="element-749" />
            </div>}
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4" data-id="element-750">
          {filteredGuards.map(guard => {
          const status = getStatus(guard.shifts);
          return <div key={guard.id} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm space-y-4 cursor-pointer hover:border-[#5a7280]/30 transition-colors" onClick={() => handleViewRoster(guard)} data-id="element-751">
                <div className="flex justify-between items-start" data-id="element-752">
                  <div className="flex items-center" data-id="element-753">
                    <div className="h-10 w-10 rounded-full bg-[#062133]/5 flex items-center justify-center mr-3" data-id="element-754">
                      <User className="h-5 w-5 text-[#062133]" data-id="element-755" />
                    </div>
                    <div data-id="element-756">
                      <h3 className="text-lg font-semibold text-[#062133]" data-id="element-757">
                        {guard.name}
                      </h3>
                      <div className="flex items-center text-xs text-[#5a7280]" data-id="element-758">
                        <MapPin className="mr-1 h-3 w-3" data-id="element-759" />
                        {guard.province}
                      </div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`} data-id="element-760">
                    {status.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 py-3 border-t border-b border-[#e5e5e5]" data-id="element-761">
                  <div data-id="element-762">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-763">
                      Current Shifts
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-764">
                      <AlertTriangle className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-765" />
                      {guard.shifts}
                    </div>
                    <p className="text-xs text-rose-500 mt-0.5" data-id="element-766">
                      +{guard.shifts - 19} over limit
                    </p>
                  </div>
                  <div data-id="element-767">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-768">
                      Grade
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-769">
                      <Briefcase className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-770" />
                      {guard.grade}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between" data-id="element-771">
                  <div data-id="element-772">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-773">
                      Primary Site
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-774">
                      <Building className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-775" />
                      {guard.site}
                    </div>
                  </div>
                  <button onClick={e => {
                e.stopPropagation();
                handleViewRoster(guard);
              }} className="inline-flex items-center px-3 py-2 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-lg text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133]" data-id="element-776">
                    <Calendar className="mr-1.5 h-3.5 w-3.5" data-id="element-777" />
                    View Roster
                  </button>
                </div>
              </div>;
        })}
          {filteredGuards.length === 0 && <EmptyState icon={UserCheck} title="No guards over threshold" description="All guards are within the 19-shift monthly limit" data-id="element-778" />}
        </div>
      </div>

      {/* Guard Roster Modal */}
      <GuardRosterModal isOpen={selectedGuard !== null} guardName={selectedGuard?.name || ''} guardId={selectedGuard?.id || ''} grade={selectedGuard?.grade || ''} primarySite={selectedGuard?.site || ''} totalShifts={selectedGuard?.shifts || 0} variant="over-threshold" onClose={handleCloseRoster} onTakeAction={() => {
      if (selectedGuard) {
        handleCloseRoster();
        handleTakeAction(selectedGuard);
      }
    }} data-id="element-779" />

      {/* Action Panel */}
      <ActionPanel isOpen={actionGuard !== null} siteName={actionGuard?.site || ''} onClose={handleCloseAction} data-id="element-780" />
    </>;
}